import React, { useState, useEffect } from 'react';
import { Modal, notification, Skeleton, Table, Tooltip } from 'antd';
import { Breadcrumbs } from 'components/Atom/breadcumbs';
import { ResetButton, Button } from 'components/Atom/Button';
import { PageHeader } from 'components/Atom/PageHeader';
import '../assets/style.css';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import DateRangePicker from 'components/Atom/DateRange';
import { TIMEZONES } from 'utils/constant';
import { useQuery } from 'react-query';
import { handleDateRangeFormat, formatAmount, convertToStoreTimezone } from 'utils/services';
import { getDeclineListStats, getSingleListDetails, getRuleListStats, getSingleRuleDetails, getXShieldStats } from 'api/xShields';
import { checkUserPermissions } from 'utils/services';
import { USER_ROLES } from 'utils/constant';
import ShortenId from 'components/Atom/ShortenId';

export const PerformanceDashboard = () => {
  const store = useSelector((state) => state.company.store);
  const [dateRange, setDateRange] = useState([]);
  const [open, setOpen] = useState(false);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [declineListType, setDeclineListType] = useState('');
  const [ruleId, setRuleId] = useState('');
  const [queryParams, setQueryParams] = useState(null);
  const onDateSearch = () => {
    if (dateRange === null || dateRange?.length === 0) {
      notification.error({ message: 'Please enter date range', placement: 'bottomRight' });
    } else {
      const timezone = store?.isSelected ? store?.settings?.timezone || TIMEZONES.PKT : null;
      const { start_date, end_date } = handleDateRangeFormat(dateRange, timezone);
      setQueryParams({ start_date, end_date });
    }
  };
  const handleDateRange = (range) => {
    if (range === null) {
      setDateRange([]);
      setQueryParams(null);
    } else {
      setDateRange(range);
    }
  };
  const typeLabels = {
    emails: 'Email Address',
    phone_numbers: 'Phone',
    bins: 'BIN',
    ip_addresses: 'IP Address',
    card_fingerprints: 'Card Fingerprint',
  };
  const breadcrumbProps = {
    breadcrumbs: [
      {
        name: 'XPay',
        href: '/xpay',
      },
      {
        name: 'XShield',
      },
      {
        name: 'Performance',
      },
    ],
  };

  const getXShieldStatsData = async () => {
    const params = { ...queryParams, ...(store?.isSelected && { store_id: store?._id }) };
    const response = await getXShieldStats(params);
    return response?.data?.data;
  };
  const {
    data: xShieldStatsData,
    refetch: fetchXShieldStats,
    isLoading: isStatsFetching,
  } = useQuery('xShieldStatsData', getXShieldStatsData, {
    enabled: false,
    manual: true,
  });

  const getDeclineStatsData = async () => {
    const params = { ...queryParams, ...(store?.isSelected && { store_id: store?._id }) };
    const response = await getDeclineListStats(params);
    return response?.data?.data;
  };
  const {
    data: declineStatsData,
    refetch: fetchDeclineStats,
    isLoading: isFetching,
  } = useQuery('declineStatsData', getDeclineStatsData, {
    enabled: false,
    manual: true,
  });

  const getRuleStatsData = async () => {
    const params = { ...queryParams, ...(store?.isSelected && { store_id: store?._id }) };
    const response = await getRuleListStats(params);
    return response?.data?.data;
  };
  const {
    data: ruleStatsData,
    refetch: fetchRuleStats,
    isLoading: isRuleStatsFetching,
  } = useQuery('ruleStatsData', getRuleStatsData, {
    enabled: false,
    manual: true,
  });

  const getSingleListDetail = async () => {
    try {
      const params = { ...queryParams, ...(store?.isSelected && { store_id: store?._id }) };
      const response = await getSingleListDetails(params, declineListType);
      const timezone = store?.settings?.timezone || TIMEZONES.PKT;
      const listData = response?.data?.data?.[declineListType];
      if (!Array.isArray(listData)) return [];
      const result = await convertToStoreTimezone(listData, timezone);
      return result;
    } catch (error) {
      console.error('Error fetching list details:', error);
      return [];
    }
  };
  const {
    data: singleListDetails,
    refetch: fetchSingleListDetails,
    isLoading: isDetailsFetching,
  } = useQuery(`singleListDetails_${declineListType}`, getSingleListDetail, {
    enabled: false,
    manual: true,
  });

  const getSingleRuleDetail = async () => {
    try {
      const params = { ...queryParams, ...(store?.isSelected && { store_id: store?._id }) };
      const response = await getSingleRuleDetails(params, ruleId);
      const timezone = store?.settings?.timezone || TIMEZONES.PKT;
      const result = await convertToStoreTimezone(response?.data?.data, timezone);
      return result;
    } catch (error) {
      console.error('Error fetching list details:', error);
      return [];
    }
  };
  const {
    data: singleRuleDetails,
    refetch: fetchSingleRuleDetails,
    isLoading: isRuleDetailsFetching,
  } = useQuery(`singleRuleDetails_${ruleId}`, getSingleRuleDetail, {
    enabled: false,
    manual: true,
  });

  useEffect(() => {
    if (store?.features?.includes('XShield')) {
      fetchDeclineStats();
      fetchRuleStats();
      fetchXShieldStats();
    }
  }, [queryParams, store]);

  useEffect(() => {
    if (declineListType) {
      fetchSingleListDetails();
    }
  }, [declineListType]);

  useEffect(() => {
    if (ruleId) {
      fetchSingleRuleDetails();
    }
  }, [ruleId]);

  const handleRefresh = () => {
    if (dateRange?.length === 0 && queryParams === null) {
      fetchDeclineStats();
      fetchRuleStats();
      fetchXShieldStats();
    } else {
      setDateRange([]);
      setQueryParams(null);
    }
  };
  const handleViewDetails = (id) => {
    setDeclineListType(id);
    setOpen(true);
  };

  const groupByValue = (data = []) => {
    const grouped = {};
    data?.forEach((item) => {
      if (!grouped[item?.value]) {
        grouped[item?.value] = [];
      }
      grouped[item?.value]?.push(item);
    });

    return Object.entries(grouped)?.map(([value, all]) => {
      const latest = all?.reduce((a, b) => (new Date(a?.createdAt) > new Date(b?.createdAt) ? a : b));
      return {
        key: value,
        value,
        latestCreatedAt: latest?.createdAt,
        all,
      };
    });
  };

  const handleRuleDetails = (id) => {
    setRuleId(id);
    setRuleOpen(true);
  };
  const declineStatscolumns = [
    {
      title: 'Type',
      render: (value) => typeLabels[value?._id] || value?._id,
      key: 'type',
    },
    {
      title: 'Total Declined Transactions',
      render: (value) => value?.totalTransactions?.toLocaleString() || 0,
      key: 'transactions',
    },
    {
      title: 'Total Declined Amount',
      render: (value) => `${store?.settings?.currency || 'PKR'} ${formatAmount(value?.totalAmount || '0', store?.settings?.currency)}`,
      key: 'amount',
    },
    ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN])
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (data) => {
              return (
                <div className='flex items-center gap-2'>
                  <div type='primary' className='table-action-btn' onClick={() => handleViewDetails(data?._id)} style={{ cursor: 'pointer' }}>
                    View Details
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
  ];
  const ruleStatscolumns = [
    {
      title: 'Name',
      render: (value) => (
        <div
          style={{
            maxWidth: 350,
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}>
          {value?.name}
        </div>
      ),
      key: 'name',
    },
    {
      title: 'Total Declined Transactions',
      render: (value) => value?.totalBlockedTransactions?.toLocaleString() || 0,
      key: 'transactions',
    },
    {
      title: 'Total Declined Amount',
      render: (value) => `${store?.settings?.currency || 'PKR'} ${formatAmount(value?.totalBlockedAmount || '0', store?.settings?.currency)}`,
      key: 'amount',
    },
    ...(checkUserPermissions([USER_ROLES.XPAY_ADMIN, USER_ROLES.XPAY_RESTRICTED_ADMIN])
      ? [
          {
            title: 'Action',
            key: 'action',
            render: (data) => {
              return (
                <div className='flex items-center gap-2'>
                  <div type='primary' className='table-action-btn' onClick={() => handleRuleDetails(data?._id)} style={{ cursor: 'pointer' }}>
                    View Details
                  </div>
                </div>
              );
            },
          },
        ]
      : []),
  ];

  const singleListColumns = [
    {
      title: 'ID',
      key: 'id',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Value',
      render: (value) => (value?.length > 30 ? <ShortenId value={value} /> : value),
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: 'Latest Timestamp',
      dataIndex: 'latestCreatedAt',
      key: 'latestCreatedAt',
      render: (value) => value,
    },
  ];

  const ruleDetailColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Intent ID',
      render: (value) => <ShortenId value={value?.pi_id} /> || '',
      key: 'pi_id',
    },
    {
      title: 'Amount',
      render: (value) => formatAmount(value?.amount || '0', store?.settings?.currency),
      key: 'amount',
    },
    {
      title: 'Timestamp',
      render: (value) => value?.createdAt || '',
      key: 'createdAt',
    },
  ];

  return (
    <>
      <div className='xshield' style={{ padding: '0px', width: '100%' }}>
        <div className='header-container'>
          <Breadcrumbs {...breadcrumbProps} />
          <div className='headerPage'>
            <div className=''>
              <PageHeader label={'Performance'} button={<ResetButton onClick={isStatsFetching ? '' : handleRefresh} loading={isStatsFetching} />} />
            </div>
          </div>
        </div>
        <div className='heading heading-line1'>
          <div className='child1 mb-5' style={{ marginTop: '24px' }}>
            <DateRangePicker className='custom-range mr-3 h-12' onChange={handleDateRange} value={dateRange} onClick={onDateSearch} title={'Apply'} />
          </div>
        </div>
        <div className='mt-8'>
          <h2 className='container-heading mb-4'>RULES RISK STATISTICS</h2>
        </div>
        <div className='fraud-summary-cards'>
          {!xShieldStatsData ? (
            <>
              {[...Array(1)]?.map((_, index) => (
                <div className='fraud-card' key={index}>
                  <Skeleton active paragraph={{ rows: 1 }} />
                </div>
              ))}
            </>
          ) : (
            <>
              <div className='fraud-card'>
                <div className='fraud-card-header'>
                  <span className='fraud-title'>
                    Total fraud
                    <Tooltip title='The total fraud rate is the total amount of fraud reported, compared to total sales over the selected date range.'>
                      <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                    </Tooltip>
                  </span>
                </div>
                <div className='fraud-amount'>
                  {formatAmount(xShieldStatsData?.flaggedAmount || '0', store?.settings?.currency)} <span className='fraud-currency'>{store?.settings?.currency || 'PKR'}</span>
                </div>
                <div className='fraud-meta'>
                  <div className='fraud-rate'>
                    <div className='meta-label'>Rate</div>
                    <div className='meta-value'>{xShieldStatsData?.totalRecords ? (xShieldStatsData?.flaggedCount / xShieldStatsData?.totalRecords).toFixed(3) : '0.00'}%</div>
                  </div>
                  <div>
                    <div className='meta-label'>Transactions</div>
                    <div className='meta-value'>{xShieldStatsData?.flaggedCount || '0'}</div>
                  </div>
                </div>
              </div>
              {/*   <div className='fraud-card'>
                <div className='fraud-card-header'>
                  <span className='fraud-title'>
                    Declined by Pre-auth
                    <Tooltip title='This is the total transaction amount declined by the risk engine at the Pre-auth stage. The rate is the total transaction amount declined compared to the total transaction amount attempted'>
                      <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                    </Tooltip>
                  </span>
                </div>
                <div className='fraud-amount'>
                  {formatAmount(xShieldStatsData?.preAuthFlaggedAmount || '0', store?.settings?.currency)} <span className='fraud-currency'>{store?.settings?.currency || 'PKR'}</span>
                </div>
                <div className='fraud-meta'>
                  <div className='fraud-rate'>
                    <div className='meta-label'>Rate</div>
                    <div className='meta-value'>{xShieldStatsData?.preAuthTotal ? (xShieldStatsData?.preAuthFlaggedCount / xShieldStatsData?.preAuthTotal).toFixed(5) : '0.00'}%</div>
                  </div>
                  <div>
                    <div className='meta-label'>Transactions</div>
                    <div className='meta-value'>{xShieldStatsData?.preAuthFlaggedCount || '0'}</div>
                  </div>
                </div>
              </div>
              <div className='fraud-card'>
                <div className='fraud-card-header'>
                  <span className='fraud-title'>
                    Voided by Post-auth
                    <Tooltip title='This is the total transaction amount voided by the risk engine at the Post-auth stage. The rate is the total transaction amount voided compared to the total transaction amount attempted'>
                      <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                    </Tooltip>
                  </span>
                </div>
                <div className='fraud-amount'>
                  {formatAmount(xShieldStatsData?.postAuthFlaggedAmount || '0', store?.settings?.currency)} <span className='fraud-currency'>{store?.settings?.currency || 'PKR'}</span>
                </div>
                <div className='fraud-meta'>
                  <div className='fraud-rate'>
                    <div className='meta-label'>Rate</div>
                    <div className='meta-value'>{xShieldStatsData?.postAuthTotal ? (xShieldStatsData?.postAuthFlaggedCount / xShieldStatsData?.postAuthTotal).toFixed(5) : '0.00'}%</div>
                  </div>
                  <div>
                    <div className='meta-label'>Transactions</div>
                    <div className='meta-value'>{xShieldStatsData?.postAuthFlaggedCount || '0'}</div>
                  </div>
                </div>
              </div> */}
            </>
          )}
        </div>
        <div className='mt-2'>
          <h2 className='container-heading mb-4'>RULES</h2>
        </div>
        <div className='table-container  overflow-x-auto custom-table mb-6'>
          <Table
            columns={
              isRuleStatsFetching
                ? ruleStatscolumns?.map((column) => {
                    return {
                      ...column,
                      render: () => {
                        return <Skeleton active={true} paragraph={false} key={column?.key} />;
                      },
                    };
                  })
                : ruleStatscolumns
            }
            dataSource={
              isRuleStatsFetching
                ? [...Array(5)].map((_, index) => ({
                    key: `key${index}`,
                  }))
                : ruleStatsData
            }
            pagination={false}
          />
        </div>
        <div className='mt-2'>
          <h2 className='container-heading mb-4'>BLOCK LIST</h2>
        </div>
        <div className='table-container  overflow-x-auto custom-table mb-6'>
          <Table
            columns={
              isFetching
                ? declineStatscolumns?.map((column) => {
                    return {
                      ...column,
                      render: () => {
                        return <Skeleton active={true} paragraph={false} key={column?.key} />;
                      },
                    };
                  })
                : declineStatscolumns
            }
            dataSource={
              isFetching
                ? [...Array(5)].map((_, index) => ({
                    key: `key${index}`,
                  }))
                : declineStatsData
            }
            pagination={false}
          />
        </div>
        <Modal
          trigger='click'
          centered
          open={open}
          width={window.innerWidth < 768 ? 350 : 600}
          footer={null}
          destroyOnClose
          onCancel={() => {
            setOpen(false);
            setDeclineListType('');
          }}>
          <div className='mobileVersion'>
            <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
              {'Block List Details'}
            </div>
            <div className='overflow-x-auto custom-table' style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <Table
                loading={isDetailsFetching}
                dataSource={groupByValue(singleListDetails)}
                columns={singleListColumns}
                expandable={{
                  expandedRowRender: (record) => (
                    <Table
                      columns={[
                        {
                          title: 'ID',
                          dataIndex: 'id',
                          key: 'id',
                          width: '100px',
                          render: (_, __, index) => index + 1,
                        },
                        {
                          title: 'Timestamp',
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          render: (value) => value,
                        },
                      ]}
                      dataSource={record?.all}
                      size='small'
                      pagination={false}
                    />
                  ),
                  rowExpandable: (record) => record?.all?.length > 1,
                }}
                scroll={{ x: 'auto' }}
                pagination={false}
              />
            </div>
          </div>
        </Modal>
        <Modal
          trigger='click'
          centered
          open={ruleOpen}
          width={window.innerWidth < 768 ? 350 : 600}
          footer={null}
          destroyOnClose
          onCancel={() => {
            setRuleOpen(false);
            setRuleId('');
          }}>
          <div className='mobileVersion'>
            <div className='font-semibold text-base' style={{ marginBottom: '12px' }}>
              {'Rule Details'}
            </div>
            <div className='overflow-x-auto custom-table' style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <Table loading={isRuleDetailsFetching} dataSource={singleRuleDetails} columns={ruleDetailColumns} scroll={{ x: 'auto' }} pagination={false} />
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};
